import * as React from "react";
import {
  REVEAL_DURATION_MS,
  createStaggerClock,
  prefersReducedMotion,
  shouldHideForReveal,
} from "./motion-logic";

/**
 * Reveal — content that fades and rises into place the first time it scrolls
 * into view. RevealGroup cascades the Reveals inside it.
 *
 *   <RevealGroup as="ul" className="grid gap-6 sm:grid-cols-3">
 *     {items.map((item) => <Reveal as="li" key={item.id}>…</Reveal>)}
 *   </RevealGroup>
 *
 * WHY IT NEVER TOUCHES THE MARKUP. The sites are prerendered by snapshotting
 * the live DOM in a headless browser (scripts/prerender.mjs). An inline
 * `opacity: 0` would be baked into the HTML that search engines read and that
 * no-JS visitors see. So the hidden state is held with the Web Animations API
 * (`element.animate`), which lives outside the DOM: the element's HTML and
 * attributes are byte-for-byte what was rendered, and the snapshot shows every
 * word.
 *
 * WHAT IS NEVER HIDDEN. Anything with any part on screen when the page starts
 * (above the fold), anything when the visitor prefers reduced motion, and
 * anything in a browser without IntersectionObserver. Those simply render.
 * Only content wholly below the fold waits for its entrance — once, then it is
 * left alone. Opacity + transform only, so nothing around it moves.
 */

type RevealTag =
  | "div"
  | "section"
  | "article"
  | "aside"
  | "header"
  | "footer"
  | "li"
  | "ul"
  | "ol"
  | "span"
  | "p"
  | "figure";

const useIsoLayoutEffect = typeof window === "undefined" ? React.useEffect : React.useLayoutEffect;

/** Set by RevealGroup; hands each Reveal its place in the cascade. */
const RevealGroupContext = React.createContext<{ claim(): number } | null>(null);

export interface RevealProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  /** The element to render. Default `div`. */
  as?: RevealTag;
  /** Extra wait before the entrance starts, in ms. Default 0. */
  delay?: number;
  /** How far below its resting place it starts, in px. Default 16. */
  y?: number;
  className?: string;
}

export function Reveal({ children, as = "div", delay = 0, y = 16, ...rest }: RevealProps) {
  const ref = React.useRef<HTMLElement | null>(null);
  const group = React.useContext(RevealGroupContext);

  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof el.animate !== "function") return;
    const hasObserver = typeof IntersectionObserver === "function";
    if (
      !shouldHideForReveal({
        top: el.getBoundingClientRect().top,
        viewportHeight: window.innerHeight,
        reducedMotion: prefersReducedMotion(),
        hasObserver,
      })
    ) {
      return;
    }

    const hiddenFrame: Keyframe = { opacity: 0, transform: `translate3d(0, ${y}px, 0)` };
    // Held until the entrance — outside the DOM, so nothing is serialised.
    const hold = el.animate([hiddenFrame, hiddenFrame], { duration: 1, fill: "forwards" });
    let entrance: Animation | null = null;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        // Asked for less movement since the page opened: just show it.
        if (prefersReducedMotion()) {
          hold.cancel();
          return;
        }
        const wait = delay + (group ? group.claim() : 0);
        entrance = el.animate([hiddenFrame, { opacity: 1, transform: "none" }], {
          duration: REVEAL_DURATION_MS,
          delay: wait,
          easing: "ease-out",
          fill: "backwards",
        });
        // Swap in the same task, so no frame shows it un-hidden in between.
        hold.cancel();
      },
      { rootMargin: "0px 0px -8% 0px" },
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      hold.cancel();
      entrance?.cancel();
    };
    // Measured once, on mount: an entrance happens at most once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return React.createElement(as, { ref, ...rest }, children);
}

export interface RevealGroupProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  /** Gap between neighbours that enter together, in ms. Default 80. */
  stagger?: number;
  /** The element to render. Default `div`. */
  as?: RevealTag;
  className?: string;
}

/**
 * Cascades the Reveals inside it: those that come into view together enter
 * `stagger` ms apart, in page order. One that arrives on its own later starts
 * straight away rather than queueing behind the ones already shown. A nested
 * RevealGroup runs its own cascade.
 */
export function RevealGroup({ children, stagger = 80, as = "div", ...rest }: RevealGroupProps) {
  const clock = React.useMemo(
    () => createStaggerClock(stagger, () => performance.now()),
    [stagger],
  );
  return (
    <RevealGroupContext.Provider value={clock}>
      {React.createElement(as, rest, children)}
    </RevealGroupContext.Provider>
  );
}
